import Image from 'next/image'
import { cx } from '@/lib/ui'
import type { Shot } from './content'

/**
 * A screenshot in a window frame, so a crop of the app reads as the app
 * and not as a stray rectangle. The frame is the panel surface with a thin
 * title strip; the phone variant is the same surface with a tighter radius
 * and no strip.
 *
 * `next/image` with a static import gives the intrinsic size (no layout
 * shift) and a blur placeholder for free.
 */
export function Screenshot({
  shot,
  priority = false,
  sizes = '(min-width: 1024px) 60vw, 100vw',
  className,
}: {
  shot: Shot
  priority?: boolean
  sizes?: string
  className?: string
}) {
  if (shot.phone) {
    return (
      <figure
        className={cx(
          'panel mx-auto w-full max-w-[16rem] overflow-hidden rounded-[2rem] shadow-[var(--shadow-float)]',
          className,
        )}
      >
        <Image
          src={shot.image}
          alt={shot.alt}
          sizes="16rem"
          placeholder="blur"
          priority={priority}
          className="block h-auto w-full"
        />
      </figure>
    )
  }
  return (
    <figure
      className={cx(
        'panel overflow-hidden rounded-[var(--radius-panel)] shadow-[var(--shadow-float)]',
        shot.narrow && 'mx-auto w-full max-w-[18rem]',
        className,
      )}
    >
      <div
        aria-hidden="true"
        className="flex h-8 items-center gap-1.5 border-b border-[var(--border)] bg-[var(--surface-raised)] px-3"
      >
        <span className="h-2 w-2 rounded-full bg-hover-strong" />
        <span className="h-2 w-2 rounded-full bg-hover-strong" />
        <span className="h-2 w-2 rounded-full bg-hover-strong" />
        <span className="mx-auto rounded-full bg-surface/80 px-3 py-0.5 font-mono text-[10px] text-muted">
          routr.mfalmestudio.com
        </span>
        <span className="w-[2.25rem]" />
      </div>
      <Image
        src={shot.image}
        alt={shot.alt}
        sizes={shot.narrow ? '18rem' : sizes}
        placeholder="blur"
        priority={priority}
        className="block h-auto w-full"
      />
    </figure>
  )
}
